import React from "react";

import type { PolymorphicBoxProps } from "~/components/polymorphic-box";
import { type VariantProp, cv } from "~/tools/class-variance";
import { type Scope, useScope } from "~/tools/impulse";
import type { ImpulseForm } from "~/tools/impulse-form";
import { isFunction } from "~/tools/is-function";
import { mapChildren } from "~/tools/map-children";
import type { OneOrMany } from "~/tools/one-or-many";

import { FormFieldContextProvider, useInitFormFieldContext } from "./_context";
import { FallbackErrorMessage } from "./_fallback-error-message";
import styles from "./_form.module.css";

const DEFAULT_ELEMENT = "div" satisfies React.ElementType;

const variants = cv(styles.formField, {
	variants: {
		layout: {
			horizontal: styles.horizontal,
			vertical: styles.vertical,
		},
	},

	defaultVariants: {
		layout: "vertical",
	},
});

export type FormFieldLayout = VariantProp<typeof variants, "layout">;

export type FormFieldRenderer<TForm extends ImpulseForm> = (scope: Scope, state: TForm) => React.ReactNode;

export interface FormFieldProps<TForm extends ImpulseForm> extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
	/**
	 * @default "vertical"
	 */
	layout?: FormFieldLayout;
	state: TForm;
	children: OneOrMany<React.ReactNode | FormFieldRenderer<TForm>>;
}

export const FormField = React.forwardRef(
	(
		{
			el: Root = DEFAULT_ELEMENT,
			layout,
			state,
			className,
			children,
			...props
		}: PolymorphicBoxProps<React.ElementType, FormFieldProps<ImpulseForm>>,
		ref: React.ForwardedRef<HTMLDivElement>,
	) => {
		const scope = useScope();
		const context = useInitFormFieldContext(state);

		const content = mapChildren(children, (child, index) => {
			if (!isFunction(child)) {
				return child;
			}

			const node = child(scope, state);

			if (!React.isValidElement(node)) {
				return node;
			}

			// it is ok to assign index as the key here, because the children are not dynamic
			return <React.Fragment key={index}>{node}</React.Fragment>;
		});

		return (
			<Root className={variants({ layout, className })} ref={ref} {...props}>
				<FormFieldContextProvider value={context}>{content}</FormFieldContextProvider>

				<FallbackErrorMessage errorMessageDescendants={context.errorMessageDescendants} state={state} />
			</Root>
		);
	},
) as {
	<TForm extends ImpulseForm, TElement extends React.ElementType = typeof DEFAULT_ELEMENT>(
		props: PolymorphicBoxProps<TElement, FormFieldProps<TForm>>,
	): React.ReactElement;

	displayName?: string;
};

if (import.meta.env.DEV) {
	FormField.displayName = "FormField";
}
